import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Banknote, Check, Lock, ShieldAlert } from "lucide-react";
import { API, inr, usePageTitle, useShop } from "@/context/ShopContext";

const EMPTY = {
  name: "", email: "", phone: "",
  address: "", city: "", state: "", pincode: "", country: "India",
};

export default function Checkout() {
  usePageTitle("Checkout");
  const navigate = useNavigate();
  const { cartItems, subtotal, clearCart, settings } = useShop();
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [order, setOrder] = useState(null);
  const [payMethod, setPayMethod] = useState("payu");
  const [giftNote, setGiftNote] = useState("");
  const isFridayIST = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" })).getDay() === 5;
  const shipping = subtotal >= settings.free_shipping_threshold || subtotal === 0 ? 0 : settings.shipping_charge;
  const total = subtotal + shipping;
  const codEligible = total >= 1000;
  const method = payMethod === "cod" && codEligible ? "cod" : "payu";

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const placeOrder = async (e) => {
    e.preventDefault();
    setStatus("placing");
    try {
      const r = await fetch(`${API}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer: { name: form.name, email: form.email, phone: form.phone },
          address: { address: form.address, city: form.city, state: form.state, pincode: form.pincode, country: form.country },
          items: cartItems.map((i) => ({ id: i.id, qty: i.qty, selections: i.selections || [] })),
          payment_method: method,
          gift_note: giftNote.trim() || null,
        }),
      });
      if (!r.ok) throw new Error();
      const data = await r.json();
      if (settings.payment_mode === "live" && method === "payu") {
        localStorage.setItem("lamsah_pending_order", data.order_number);
        localStorage.setItem("lamsah_pending_email", form.email.trim().toLowerCase());
        const pr = await fetch(`${API}/payments/payu/start`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ order_number: data.order_number }),
        });
        const pay = await pr.json();
        if (!pr.ok) throw new Error();
        const f = document.createElement("form");
        f.method = "POST";
        f.action = pay.payment_url;
        Object.entries(pay.fields).forEach(([name, value]) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = name;
          input.value = String(value ?? "");
          f.appendChild(input);
        });
        document.body.appendChild(f);
        f.submit();
        return;
      }
      setOrder(data);
      clearCart();
      window.scrollTo(0, 0);
    } catch {
      setStatus("error");
    }
  };

  if (order) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 md:py-32 text-center" data-testid="order-confirmation">
        <span className="w-16 h-16 rounded-full bg-sage flex items-center justify-center mx-auto">
          <Check className="w-7 h-7 text-espresso" strokeWidth={1.5} />
        </span>
        <h1 className="font-serif text-3xl sm:text-4xl font-light mt-8">Thank You for Choosing LAMSAH</h1>
        <p className="text-sm text-espresso/70 mt-4 leading-relaxed">
          Your order <span className="font-medium text-espresso" data-testid="order-number">{order.order_number}</span> has
          been received. A confirmation will be sent to your email.
        </p>
        {order.payment_method === "cod" ? (
          <p className="text-xs text-taupe mt-3" data-testid="cod-order-note">
            Cash on Delivery — please keep {inr(order.total)} ready when your LAMSAH box arrives.
          </p>
        ) : settings.payment_mode !== "live" && (
          <p className="text-xs text-taupe mt-3" data-testid="demo-order-note">
            Demo checkout — no payment was charged for this order.
          </p>
        )}
        <div className="flex flex-wrap justify-center gap-4 mt-10">
          <Link to="/shop" className="btn-primary" data-testid="continue-shopping">Continue Shopping</Link>
          <Link to="/" className="btn-secondary" data-testid="back-home">Back Home</Link>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-32 text-center" data-testid="checkout-empty">
        <p className="font-serif text-3xl">Your bag is empty</p>
        <p className="text-sm text-espresso/60 mt-3">Add something beautiful before checking out.</p>
        <Link to="/shop" className="btn-primary mt-8" data-testid="checkout-shop-link">Shop Hijabs</Link>
      </div>
    );
  }

  const field = (key, label, props = {}) => (
    <div>
      <label htmlFor={`checkout-${key}`} className="overline-label block mb-3">{label}</label>
      <input id={`checkout-${key}`} data-testid={`checkout-${key}`} required value={form[key]} onChange={set(key)} className="input-lux" {...props} />
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 md:py-20" data-testid="checkout-page">
      <header className="mb-12">
        <p className="overline-label mb-4">Almost Yours</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight">Checkout</h1>
      </header>

      <form onSubmit={placeOrder} className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-12">
          <section>
            <h2 className="font-serif text-2xl mb-6">Contact Information</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {field("name", "Full Name", { placeholder: "Your full name" })}
              {field("email", "Email", { type: "email", placeholder: "you@example.com" })}
              <div className="sm:col-span-2">{field("phone", "Phone", { type: "tel", placeholder: "+91 …" })}</div>
            </div>
          </section>

          <section>
            <h2 className="font-serif text-2xl mb-6">Delivery Address</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">{field("address", "Address", { placeholder: "Flat, street, area" })}</div>
              {field("city", "City")}
              {field("state", "State")}
              {field("pincode", "PIN / Postal Code", { inputMode: "numeric" })}
              {field("country", "Country")}
            </div>
          </section>

          <section>
            <div className={`border px-5 py-4 mb-8 ${isFridayIST ? "border-rose/40 bg-rose/5" : "border-beige bg-white"}`} data-testid="friday-gift-banner">
              <p className="overline-label mb-1">Friday Gift</p>
              <p className="text-sm text-espresso/80">
                {isFridayIST
                  ? "It's Friday — a complimentary LAMSAH mini attar will be gifted with your hijab today. ♥"
                  : "Every Friday, orders include a complimentary LAMSAH mini attar gifted with your hijab. Order on a Friday to receive yours."}
              </p>
            </div>
            <h2 className="font-serif text-2xl mb-2">Gift Note <span className="text-sm text-taupe font-sans">(optional)</span></h2>
            <p className="text-sm text-espresso/60 mb-5">Sending this as a gift? We'll tuck a hand-written style card with your message inside the LAMSAH box.</p>
            <textarea
              id="checkout-gift-note"
              data-testid="checkout-gift-note"
              value={giftNote}
              onChange={(e) => setGiftNote(e.target.value.slice(0, 300))}
              rows={3}
              maxLength={300}
              placeholder="e.g. Happy birthday, Ayesha — every shade of love. ♥"
              className="input-lux w-full resize-none"
            />
            <p className="text-[11px] text-taupe mt-2 text-right">{giftNote.length}/300</p>
          </section>

          <section>
            <h2 className="font-serif text-2xl mb-6">Payment</h2>
            {settings.payment_mode === "live" ? (
              <div className="space-y-4">
                <label className={`border bg-white p-6 flex gap-4 cursor-pointer transition-colors ${method === "payu" ? "border-espresso" : "border-beige"}`} data-testid="pay-option-payu">
                  <input type="radio" name="payment-method" className="w-4 h-4 accent-[#2B211C] mt-1 shrink-0" checked={method === "payu"} onChange={() => setPayMethod("payu")} data-testid="pay-radio-payu" />
                  <div>
                    <p className="text-sm font-medium flex items-center gap-2"><Lock className="w-4 h-4" strokeWidth={1.5} /> Pay Online — PayU</p>
                    <p className="text-sm text-espresso/70 leading-relaxed mt-1">
                      UPI, credit / debit card or netbanking on PayU's secure page. Your card
                      details are never shared with LAMSAH.
                    </p>
                  </div>
                </label>
                <label className={`border bg-white p-6 flex gap-4 transition-colors ${!codEligible ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} ${method === "cod" ? "border-espresso" : "border-beige"}`} data-testid="pay-option-cod">
                  <input type="radio" name="payment-method" className="w-4 h-4 accent-[#2B211C] mt-1 shrink-0" disabled={!codEligible} checked={method === "cod"} onChange={() => setPayMethod("cod")} data-testid="pay-radio-cod" />
                  <div>
                    <p className="text-sm font-medium flex items-center gap-2"><Banknote className="w-4 h-4" strokeWidth={1.5} /> Cash on Delivery</p>
                    <p className="text-sm text-espresso/70 leading-relaxed mt-1">
                      {codEligible
                        ? "Pay in cash when your LAMSAH box arrives at your door."
                        : `Available on orders above ₹1,000 — add ${inr(1000 - total)} more to unlock.`}
                    </p>
                  </div>
                </label>
              </div>
            ) : (
              <div className="border border-dashed border-taupe/50 bg-white p-6 flex gap-4" data-testid="demo-payment-notice">
                <ShieldAlert className="w-5 h-5 text-taupe shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="text-sm font-medium">Demo checkout</p>
                  <p className="text-sm text-espresso/70 leading-relaxed mt-1">
                    The store is currently in demo mode — no payment will be processed or
                    charged. Live payments via PayU can be switched on from the owner
                    dashboard at any time.
                  </p>
                </div>
              </div>
            )}
          </section>

          {status === "error" && (
            <p className="text-sm text-rose" data-testid="checkout-error">Something went wrong — please try again.</p>
          )}
          <button type="submit" data-testid="place-order-button" disabled={status === "placing"} className="btn-primary w-full sm:w-auto">
            {status === "placing"
              ? (settings.payment_mode === "live" && method === "payu" ? "Taking you to PayU…" : "Placing Order…")
              : (settings.payment_mode !== "live"
                  ? `Place Demo Order — ${inr(total)}`
                  : method === "cod"
                    ? `Place COD Order — ${inr(total)}`
                    : `Pay Securely — ${inr(total)}`)}
          </button>
        </div>

        <div className="lg:col-span-5">
          <div className="bg-white border border-beige p-8 lg:sticky lg:top-28" data-testid="checkout-summary">
            <h2 className="font-serif text-2xl">Your Bag</h2>
            <div className="divide-y divide-beige mt-6">
              {cartItems.map((item) => {
                const { product, qty, key } = item;
                return (
                <div key={key} className="py-4 flex gap-4 items-center">
                  <span className="w-14 shrink-0 bg-beige aspect-[4/5] overflow-hidden">
                    <img src={product.images[0]} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif leading-snug">{product.name}</p>
                    <p className="text-[11px] uppercase tracking-[0.15em] text-taupe mt-0.5">{product.shade} × {qty}</p>
                    {item.selections && item.selections.length > 0 && (
                      <p className="text-[11px] text-taupe mt-1 leading-relaxed">{item.selections.join(" · ")}</p>
                    )}
                  </div>
                  <p className="text-sm">{inr(product.price * qty)}</p>
                </div>
                );
              })}
            </div>
            <div className="space-y-3 mt-6 pt-6 border-t border-beige text-sm">
              <div className="flex justify-between"><span className="text-espresso/70">Subtotal</span><span>{inr(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-espresso/70">Shipping</span><span>{shipping === 0 ? "Complimentary" : inr(shipping)}</span></div>
              <div className="flex justify-between pt-3 border-t border-beige text-base font-medium">
                <span>Total</span><span data-testid="checkout-total">{inr(subtotal + shipping)}</span>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
